import React, { useState, useRef, useEffect } from 'react';
import './SignatureModal.css';

const SignatureModal = ({ isOpen, onClose, onConfirm, items = [] }) => {
  const [operatorSignature, setOperatorSignature] = useState('');
  const [receiverSignature, setReceiverSignature] = useState('');
  const [operatorName, setOperatorName] = useState('');
  const [receiverName, setReceiverName] = useState('');
  
  const operatorCanvasRef = useRef(null);
  const receiverCanvasRef = useRef(null);
  
  const [isDrawing, setIsDrawing] = useState(false);
  const [currentCanvas, setCurrentCanvas] = useState(null);

  // Limpar assinaturas quando o modal abre
  useEffect(() => {
    if (isOpen) {
      setOperatorSignature('');
      setReceiverSignature('');
      setOperatorName('');
      setReceiverName('');
      clearCanvas(operatorCanvasRef.current);
      clearCanvas(receiverCanvasRef.current);
    }
  }, [isOpen, items]); // Adicionar items como dependência

  const clearCanvas = (canvas) => {
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx.clearRect(0, 0, canvas.width, canvas.height);
    }
  };

  const startDrawing = (e, canvasRef) => {
    setIsDrawing(true);
    setCurrentCanvas(canvasRef);
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.beginPath();
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top);
  };

  const draw = (e) => {
    if (!isDrawing || !currentCanvas) return;
    
    const canvas = currentCanvas.current;
    const rect = canvas.getBoundingClientRect();
    const ctx = canvas.getContext('2d');
    
    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top);
    ctx.stroke();
  };

  const stopDrawing = () => {
    setIsDrawing(false);
    setCurrentCanvas(null);
  };

  const clearSignature = (canvasRef, setSignature) => {
    clearCanvas(canvasRef.current);
    setSignature('');
  };

  const saveSignature = (canvasRef, setSignature) => {
    const canvas = canvasRef.current;
    const dataURL = canvas.toDataURL();
    setSignature(dataURL);
  };

  const handleConfirm = async () => {
    if (!operatorSignature || !receiverSignature) {
      alert('Por favor, colete ambas as assinaturas antes de confirmar.');
      return;
    }
    
    if (!operatorName.trim() || !receiverName.trim()) {
      alert('Por favor, preencha os nomes do operador e recebedor.');
      return;
    }

    const signatureData = {
      operator: {
        name: operatorName,
        signature: operatorSignature
      },
      receiver: {
        name: receiverName,
        signature: receiverSignature
      },
      timestamp: new Date().toISOString(),
      items: items.map(item => ({
        ...item,
        // Garantir que cada item tenha dados únicos
        cod_sis: item.cod_sis,
        part_number: item.part_number,
        descricao: item.descricao,
        localizacao_fisica: item.localizacao_fisica,
        nova_localizacao: item.nova_localizacao || item.localizacao_fisica,
        os_relacionada: item.os_relacionada,
        nova_os: item.nova_os || item.os_relacionada,
        lote: item.lote,
        novo_lote: item.novo_lote || item.lote,
        status: item.status,
        novo_status: item.novo_status || item.status
      }))
    };

    try {
      // Gerar PDF e salvar no banco
      const response = await fetch('/api/generate_pdf.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          items: items,
          signatureData: signatureData,
          itemIds: items.map(item => item.id).filter(id => id)
        })
      });

      const result = await response.json();
      
      if (result.success) {
        // Abrir PDF em nova aba
        window.open(result.pdf_url, '_blank');
        onConfirm(signatureData);
      } else {
        alert('Erro ao gerar PDF: ' + result.error);
      }
    } catch (error) {
      console.error('Erro ao gerar PDF:', error);
      alert('Erro ao gerar PDF. Tente novamente.');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="signature-modal-overlay">
      <div className="signature-modal">
        <div className="signature-modal-header">
          <h2>📝 Comprovante de Entrega de Peças</h2>
          <div className="signature-timestamp">
            Data/Hora: {new Date().toLocaleString('pt-BR')}
          </div>
        </div>

        <div className="signature-modal-body">
          {/* Itens Movimentados */}
          <div className="signature-items-section">
            <h3>Itens Movimentados:</h3>
            <div className="signature-items-table">
              <table>
                <thead>
                  <tr>
                    <th>COD.SIS</th>
                    <th>PN</th>
                    <th>Local Atual</th>
                    <th>Local Novo</th>
                    <th>OS (Atual)</th>
                    <th>OS (Nova)</th>
                    <th>Lote Atual</th>
                    <th>Lote Novo</th>
                    <th>Status Atual</th>
                    <th>Status Novo</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item, index) => (
                    <tr key={index}>
                      <td className="cod-sis">{item.cod_sis}</td>
                      <td>{item.part_number}</td>
                      <td>{item.localizacao_fisica || '-'}</td>
                      <td>{item.nova_localizacao || item.localizacao_fisica || '-'}</td>
                      <td>{item.os_relacionada || '-'}</td>
                      <td>{item.nova_os || item.os_relacionada || '-'}</td>
                      <td>{item.lote || '-'}</td>
                      <td>{item.novo_lote || item.lote || '-'}</td>
                      <td>{item.status || '-'}</td>
                      <td>{item.novo_status || item.status || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Termo de Responsabilidade */}
          <div className="signature-terms">
            <p>
              Declaro para os devidos fins que recebi os itens listados acima em perfeitas condições 
              e concordo com a movimentação de estoque registrada. A responsabilidade pela guarda e 
              correto uso dos materiais passa a ser do recebedor a partir da data e hora desta assinatura.
            </p>
          </div>

          {/* Campos de Assinatura */}
          <div className="signature-fields">
            {/* Operador */}
            <div className="signature-field">
              <div className="signature-field-header">
                <label>Operador</label>
                <input
                  type="text"
                  placeholder="Nome do Operador"
                  value={operatorName}
                  onChange={(e) => setOperatorName(e.target.value)}
                  className="signature-name-input"
                />
              </div>
              <div className="signature-canvas-container">
                <canvas
                  ref={operatorCanvasRef}
                  width={400}
                  height={150}
                  className="signature-canvas"
                  onMouseDown={(e) => startDrawing(e, operatorCanvasRef)}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />
                <div className="signature-canvas-label">
                  Assinatura do Entregador
                </div>
                <div className="signature-canvas-actions">
                  <button
                    type="button"
                    onClick={() => clearSignature(operatorCanvasRef, setOperatorSignature)}
                    className="btn btn-outline btn-sm"
                  >
                    Limpar
                  </button>
                  <button
                    type="button"
                    onClick={() => saveSignature(operatorCanvasRef, setOperatorSignature)}
                    className="btn btn-primary btn-sm"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>

            {/* Recebedor */}
            <div className="signature-field">
              <div className="signature-field-header">
                <label>Nome do Recebedor</label>
                <input
                  type="text"
                  placeholder="Nome do Recebedor"
                  value={receiverName}
                  onChange={(e) => setReceiverName(e.target.value)}
                  className="signature-name-input"
                />
              </div>
              <div className="signature-canvas-container">
                <canvas
                  ref={receiverCanvasRef}
                  width={400}
                  height={150}
                  className="signature-canvas"
                  onMouseDown={(e) => startDrawing(e, receiverCanvasRef)}
                  onMouseMove={draw}
                  onMouseUp={stopDrawing}
                  onMouseLeave={stopDrawing}
                />
                <div className="signature-canvas-label">
                  Assinatura do Recebedor
                </div>
                <div className="signature-canvas-actions">
                  <button
                    type="button"
                    onClick={() => clearSignature(receiverCanvasRef, setReceiverSignature)}
                    className="btn btn-outline btn-sm"
                  >
                    Limpar
                  </button>
                  <button
                    type="button"
                    onClick={() => saveSignature(receiverCanvasRef, setReceiverSignature)}
                    className="btn btn-primary btn-sm"
                  >
                    Salvar
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="signature-modal-footer">
          <button
            type="button"
            onClick={onClose}
            className="btn btn-outline"
          >
            Fechar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            className="btn btn-success"
          >
            Gerar PDF
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignatureModal;
